import React, { useEffect, useMemo, useState } from 'react';
import { TopBar } from '../../components/TopBar/TopBar';
import { DonationModal } from '../../components/DonationModal/DonationModal';
import { InviteFriendsModal } from '../../components/InviteFriendsModal/InviteFriendsModal';
import { DonationTeam } from '../../shared/models/DonationTeam';
import { Project, FundraisingType } from '../../shared/models/Project';
import { Donation } from '../../shared/models/Donation';
import { DonationService } from '../../services/DonationService';
import { formatRelativeTime } from '../../utils/dateFormat';
import './DonationTeamDetailView.css';

interface Props {
  team: DonationTeam;
  project: Project;
  onBack: () => void;
  onGoHome: () => void;
  onAgreementClick?: () => void;
  onDonate?: (params: { amount: number; recurring: boolean; anonymous: boolean; teamId?: string }, project?: Project) => void;
}

const placeholder = '/placeholder.svg';

export const DonationTeamDetailView: React.FC<Props> = ({ team, project, onBack, onGoHome, onAgreementClick, onDonate }) => {
  const [donateVisible, setDonateVisible] = useState(false);
  const [inviteVisible, setInviteVisible] = useState(false);
  const [donations, setDonations] = useState<Donation[]>([]);
  const [loading, setLoading] = useState(false);
  const isStopped = project.status === 'STOPPED';

  const shareUrl = useMemo(
    () => `${window.location.origin}/?teamId=${encodeURIComponent(team.id)}`,
    [team.id]
  );

  const inviteFooterName =
    project.organization?.name || project.executingOrganization?.name || undefined;
  const inviteHeaderUrl =
    project.bigImageUrl || project.coverImageUrl || project.coverThumbUrl || undefined;

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const resp = await DonationService.getDonations({
          projectId: project.id,
          teamId: team.id,
          status: 'PAID',
          page: 1,
          limit: 10,
        });
        setDonations(resp.list || []);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [project.id, team.id]);

  return (
    <div className="team-detail-page">
      <TopBar title="一起捐" showBack onBack={onBack} />

      <div
        className="team-detail-hero"
        style={{
          backgroundImage: `linear-gradient(180deg, rgba(0,0,0,0) 0%, rgba(0,0,0,0.1) 100%), url(${project.bigImageUrl || project.coverImageUrl || project.coverThumbUrl || placeholder})`,
        }}
      />

      <div className="team-detail-body">
        <div className="team-detail-main">
          <div className="team-detail-avatar-wrap">
            {team.avatar ? (
              <img src={team.avatar} alt={team.name} className="team-detail-avatar" onError={(e) => { e.currentTarget.src = placeholder; }} />
            ) : (
              <div className="team-detail-avatar team-detail-avatar-placeholder">👥</div>
            )}
          </div>

          <div className="team-detail-name">{team.name}</div>
          <div className="team-detail-slogan">{team.slogan || '一起汇聚爱心'}</div>

        <div className="team-detail-stats-bar">
          <div className="team-detail-stat">
              <div className="team-detail-stat-label">已募集（人）</div>
              <div className="team-detail-stat-value">{team.memberCount || 0}</div>
            </div>
          <div className="team-detail-stat team-detail-stat-right">
              <div className="team-detail-stat-label">已筹款（元）</div>
              <div className="team-detail-stat-value">¥{(team.donationAmount || 0).toLocaleString()}</div>
            </div>
          </div>

          <button type="button" className="team-detail-invite-btn" onClick={() => setInviteVisible(true)}>
            邀请好友
          </button>
        </div>

        <div className="team-detail-section">
          <div className="team-detail-section-title">
            <span>捐赠项目</span>
          </div>
          <div className="team-detail-project">
            <img src={project.coverThumbUrl || project.coverImageUrl || placeholder} alt={project.title} />
            <div className="team-detail-project-content">
              <div className="team-detail-project-name">{project.title}</div>
              <div className="team-detail-project-desc">{project.description || '暂无简介'}</div>
            </div>
          </div>
        </div>

        <div className="team-detail-section">
          <div className="team-detail-section-title">
            <span>募捐动态</span>
          </div>
          {loading && <div className="team-detail-placeholder">加载中...</div>}
          {!loading && donations.length === 0 && <div className="team-detail-placeholder">暂无募捐动态</div>}
          {!loading && donations.length > 0 && (
            <div className="team-detail-donation-list">
              {donations.map((donation) => (
                <div key={donation.id} className="team-detail-donation-item">
                  <div className="team-detail-donation-name">{donation.isAnonymous ? '爱心用户' : (donation.donorNickname || '爱心人士')}</div>
                  <div className="team-detail-donation-time">{formatRelativeTime(donation.paymentTime || donation.createTime)}</div>
                  <div className="team-detail-donation-amount">{donation.amount}元</div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="team-detail-bottom">
        <button className="team-detail-btn" onClick={onGoHome}>首页</button>
        <button
          className="team-detail-btn team-detail-btn-primary"
          disabled={isStopped}
          onClick={() => {
            if (isStopped) return;
            setDonateVisible(true);
          }}
        >
          {isStopped ? '已停止募捐' : '我要捐赠'}
        </button>
      </div>

      <InviteFriendsModal
        visible={inviteVisible}
        onClose={() => setInviteVisible(false)}
        shareUrl={shareUrl}
        teamName={team.name}
        teamAvatar={team.avatar}
        headerImageUrl={inviteHeaderUrl}
        footerName={inviteFooterName}
      />

      <DonationModal
        visible={donateVisible}
        onClose={() => setDonateVisible(false)}
        onAgreementClick={onAgreementClick}
        onConfirm={({ amount, recurring, anonymous }) => onDonate?.({ amount, recurring, anonymous, teamId: team.id }, project)}
        fundraisingType={project.fundraisingType as FundraisingType | undefined}
        tiers={project.fundraisingTiers}
        supportCustomAmount={project.supportCustomAmount}
        customAmountMin={project.customAmountMin}
        customAmountMax={project.customAmountMax}
        projectTitle={project.title}
      />
    </div>
  );
};

